import React, { useState } from "react";
// ANT DESIGN COMPONENTS
import { Button, Collapse, Divider, InputNumber, Select, Switch, Tag, Tooltip } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import EditableRowCard from "./EditableRowCard";
import SelectLn2Message from "../../../../components/select/SelectLn2Message";
import FormInputText from "../../../../components/form/FormInputText";
import NodeIdEditor, { BLANK_NUMERIC_NODE_ID } from "./NodeIdEditor";
import DataValueEditor from "./DataValueEditor";
import AttributeSelect from "./AttributeSelect";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import OpcUaTextField from "./OpcUaTextField";
import { messageNameOf, nodeIdSegments, RAW_COLOR } from "./MappingsEditor";
import { categoryColor, categoryBorderColor } from "./mappingCategoryColor";
// UTILS
import { getCategory, getOptionLabel } from "../../../../../flatbuffers/types/ln2MessageOptions";
// FLATBUFFER ENUMS
import { AttributeId } from "../../../../../flatbuffers/generated/opc-ua/attribute-id";
import { FilterOperator } from "../../../../../flatbuffers/generated/opc-ua/filter-operator";
// INTERFACES
import type {
  OpcUaContentFilterElementForm,
  OpcUaEventMonitorForm,
  OpcUaEventToMessageRow,
  OpcUaFilterOperandForm,
  OpcUaNodeIdForm,
  OpcUaSimpleAttributeOperandForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Panel } = Collapse;
const { Option } = Select;

const BLANK_ATTRIBUTE_OPERAND: OpcUaSimpleAttributeOperandForm = {
  typeDefinitionNode: null,
  browsePath: [],
  attributeId: AttributeId.Value,
  indexRange: "",
};

const BLANK_FILTER_ELEMENT: OpcUaContentFilterElementForm = {
  operator: FilterOperator.Equals,
  operands: [],
};

const FILTER_OPERATOR_OPTIONS: { value: FilterOperator; label: string }[] = [
  { value: FilterOperator.Equals, label: "Equals" },
  { value: FilterOperator.IsNull, label: "IsNull" },
  { value: FilterOperator.GreaterThan, label: "GreaterThan" },
  { value: FilterOperator.LessThan, label: "LessThan" },
  { value: FilterOperator.GreaterThanOrEqual, label: "GreaterThanOrEqual" },
  { value: FilterOperator.LessThanOrEqual, label: "LessThanOrEqual" },
  { value: FilterOperator.Like, label: "Like" },
  { value: FilterOperator.Not, label: "Not" },
  { value: FilterOperator.Between, label: "Between" },
  { value: FilterOperator.InList, label: "InList" },
  { value: FilterOperator.And, label: "And" },
  { value: FilterOperator.Or, label: "Or" },
  { value: FilterOperator.Cast, label: "Cast" },
  { value: FilterOperator.InView, label: "InView" },
  { value: FilterOperator.OfType, label: "OfType" },
  { value: FilterOperator.RelatedTo, label: "RelatedTo" },
  { value: FilterOperator.BitwiseAnd, label: "BitwiseAnd" },
  { value: FilterOperator.BitwiseOr, label: "BitwiseOr" },
];

function isNodeComplete(node: OpcUaNodeIdForm): boolean {
  if (node.identifierKind === "browsepath") return (node.hops ?? []).length > 0;
  return node.identifier.trim() !== "";
}

/** Split a slash-separated browse path into its non-empty, trimmed segments. */
function parseBrowsePathText(text: string): string[] {
  return text
    .split("/")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);
}

/** One SELECT clause: which attribute of which event-type hierarchy to report, addressed by an ordered browse-name path. */
function SimpleAttributeOperandFields({
  operand,
  onChangeOperand,
}: {
  operand: OpcUaSimpleAttributeOperandForm;
  onChangeOperand: (operand: OpcUaSimpleAttributeOperandForm) => void;
}) {
  // The path is typed as text. Keeping the text locally (rather than re-deriving
  // it from the parsed segments on every keystroke) is what lets a trailing "/"
  // or a half-typed "2:" survive until the next character arrives. When the
  // operand changes from outside (a sibling clause was removed and this
  // component now shows another one), the stored path wins over the stale text.
  const [typedPath, setTypedPath] = useState(operand.browsePath.join("/"));
  const pathText =
    parseBrowsePathText(typedPath).join("/") === operand.browsePath.join("/")
      ? typedPath
      : operand.browsePath.join("/");
  const onChangeBrowsePathCsv = (csv: string) => {
    setTypedPath(csv);
    onChangeOperand({ ...operand, browsePath: parseBrowsePathText(csv) });
  };

  return (
    <div className="row">
      <div className="col-12 col-md-6 mt-2">
        <FormInputText
          inputValue={pathText}
          inputLabelName="Browse Path (slash-separated, e.g. EventType/2:Severity)"
          inputOnChange={onChangeBrowsePathCsv}
          inputVariableName="browsePath"
          isRequired={false}
          disabledInput={false}
        />
      </div>
      <div className="col-12 col-md-4 mt-2">
        <AttributeSelect
          value={operand.attributeId}
          onChange={(attributeId) => onChangeOperand({ ...operand, attributeId })}
        />
      </div>
      <div className="col-12 col-md-2 mt-2">
        <FormInputText
          inputValue={operand.indexRange}
          inputLabelName="Index Range"
          inputOnChange={(value) => onChangeOperand({ ...operand, indexRange: value })}
          inputVariableName="indexRange"
          isRequired={false}
          disabledInput={false}
        />
      </div>
      <div className="col-12 mt-2">
        <OpcUaFieldLabel label="Scope to an event type (else BaseEventType)" />
        <div style={{ marginTop: 8 }}>
          <Switch
            aria-label="Scope to an event type"
            checked={operand.typeDefinitionNode !== null}
            onChange={(enabled) =>
              onChangeOperand({
                ...operand,
                typeDefinitionNode: enabled ? { ...BLANK_NUMERIC_NODE_ID } : null,
              })
            }
          />
        </div>
        {operand.typeDefinitionNode && (
          <NodeIdEditor
            node={operand.typeDefinitionNode}
            onChangeNode={(typeDefinitionNode) =>
              onChangeOperand({ ...operand, typeDefinitionNode })
            }
            label="Event Type"
          />
        )}
      </div>
    </div>
  );
}

/**
 * A single WHERE-clause element's operand editor: a literal, a simple
 * attribute, or an `ElementOperand` (a reference by index to another element
 * in the filter's element list).
 */
function FilterOperandEditor({
  operand,
  onChangeOperand,
}: {
  operand: OpcUaFilterOperandForm;
  onChangeOperand: (operand: OpcUaFilterOperandForm) => void;
}) {
  return (
    <div
      className="mt-2"
      style={{ backgroundColor: "#161616", borderRadius: "5px", padding: "8px" }}
    >
      <Select
        className="generalStyles__width100"
        size="large"
        value={operand.kind}
        onChange={(kind: OpcUaFilterOperandForm["kind"]) => {
          if (kind === "literal") {
            onChangeOperand({ kind: "literal", value: { kind: "string", value: "" } });
          } else if (kind === "element") {
            onChangeOperand({ kind: "element", index: 0 });
          } else {
            onChangeOperand({ kind: "attribute", operand: { ...BLANK_ATTRIBUTE_OPERAND } });
          }
        }}
      >
        <Option value="literal">Literal Value</Option>
        <Option value="attribute">Simple Attribute</Option>
        <Option value="element">Element Reference</Option>
      </Select>
      {operand.kind === "element" && (
        <div className="mt-2">
          <OpcUaFieldLabel
            label="Element Index"
            tooltip="Index of another element in this filter's element list that this operand refers to (a nested sub-expression)."
          />
          <InputNumber
            aria-label="Element Index"
            className="generalStyles__width100"
            size="large"
            style={{ width: "100%", marginTop: 8 }}
            min={0}
            value={operand.index}
            onChange={(index) => onChangeOperand({ kind: "element", index: index ?? 0 })}
          />
        </div>
      )}
      {operand.kind === "literal" && (
        <div className="row">
          <DataValueEditor
            value={operand.value}
            onChange={(value) => onChangeOperand({ kind: "literal", value })}
          />
        </div>
      )}
      {operand.kind === "attribute" && (
        <SimpleAttributeOperandFields
          operand={operand.operand}
          onChangeOperand={(updated) => onChangeOperand({ kind: "attribute", operand: updated })}
        />
      )}
    </div>
  );
}

/** One flat WHERE-clause element: an operator applied to an ordered list of operands. */
function ContentFilterElementEditor({
  element,
  onChangeElement,
}: {
  element: OpcUaContentFilterElementForm;
  onChangeElement: (element: OpcUaContentFilterElementForm) => void;
}) {
  const onAddOperand = () => {
    onChangeElement({
      ...element,
      operands: [...element.operands, { kind: "literal", value: { kind: "string", value: "" } }],
    });
  };
  const onRemoveOperand = (index: number) => {
    onChangeElement({ ...element, operands: element.operands.filter((_, i) => i !== index) });
  };
  const onChangeOperandAt = (index: number, operand: OpcUaFilterOperandForm) => {
    const operands = [...element.operands];
    operands[index] = operand;
    onChangeElement({ ...element, operands });
  };

  return (
    <div
      style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "10px" }}
      className="mb-2"
    >
      <span className="generalStyles__inputLabel">Operator</span>
      <Select
        className="generalStyles__width100"
        size="large"
        value={element.operator}
        onChange={(operator: FilterOperator) => onChangeElement({ ...element, operator })}
        style={{ marginBottom: 8 }}
      >
        {FILTER_OPERATOR_OPTIONS.map((opt) => (
          <Option key={opt.value} value={opt.value}>
            {opt.label}
          </Option>
        ))}
      </Select>
      <span className="generalStyles__inputLabel">Operands ({element.operands.length})</span>
      {element.operands.map((operand, index) => (
        <div key={index} className="generalStyles__flex">
          <div style={{ width: "100%" }}>
            <FilterOperandEditor
              operand={operand}
              onChangeOperand={(o) => onChangeOperandAt(index, o)}
            />
          </div>
          <Tooltip title="Remove operand">
            <Button
              onClick={() => onRemoveOperand(index)}
              className="buttonStyle__29"
              shape="circle"
              icon={<FontAwesomeIcon icon={faTrash} />}
            />
          </Tooltip>
        </div>
      ))}
      <Button onClick={onAddOperand} className="buttonStyle__28 mt-2">
        <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
        Add Operand
      </Button>
    </div>
  );
}

/** Event/alarm subscription: which node, SELECT clauses, a flat WHERE-clause element list, subscription name, and queue size. */
function EventMonitorForm({
  monitor,
  onChangeMonitor,
  disabled = false,
}: {
  monitor: OpcUaEventMonitorForm;
  onChangeMonitor: (monitor: OpcUaEventMonitorForm) => void;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");
  const onAddSelectClause = () => {
    onChangeMonitor({
      ...monitor,
      selectClauses: [...monitor.selectClauses, { ...BLANK_ATTRIBUTE_OPERAND }],
    });
  };
  const onRemoveSelectClause = (index: number) => {
    onChangeMonitor({
      ...monitor,
      selectClauses: monitor.selectClauses.filter((_, i) => i !== index),
    });
  };
  const onChangeSelectClauseAt = (index: number, operand: OpcUaSimpleAttributeOperandForm) => {
    const selectClauses = [...monitor.selectClauses];
    selectClauses[index] = operand;
    onChangeMonitor({ ...monitor, selectClauses });
  };

  const onAddFilterElement = () => {
    onChangeMonitor({
      ...monitor,
      whereClause: [...monitor.whereClause, { ...BLANK_FILTER_ELEMENT }],
    });
  };
  const onRemoveFilterElement = (index: number) => {
    onChangeMonitor({ ...monitor, whereClause: monitor.whereClause.filter((_, i) => i !== index) });
  };
  const onChangeFilterElementAt = (index: number, element: OpcUaContentFilterElementForm) => {
    const whereClause = [...monitor.whereClause];
    whereClause[index] = element;
    onChangeMonitor({ ...monitor, whereClause });
  };

  return (
    // While the create form is parked behind an in-place edit, the nested
    // clause editors (which have no disabled prop of their own) are frozen too.
    <div
      aria-disabled={disabled || undefined}
      style={disabled ? { pointerEvents: "none", opacity: 0.6 } : undefined}
    >
      <NodeIdEditor
        node={monitor.node}
        onChangeNode={(node) => onChangeMonitor({ ...monitor, node })}
        disabled={disabled}
      />
      <div className="row mt-2">
        <div className="col-12 col-md-6">
          <OpcUaTextField
            label="Subscription Name"
            value={monitor.subscriptionName}
            onChange={(value) => onChangeMonitor({ ...monitor, subscriptionName: value })}
            tooltip={t("general.opcUaEventMonitorSubscriptionHelp")}
            disabled={disabled}
          />
        </div>
        <div className="col-12 col-md-6">
          <OpcUaFieldLabel label="Queue Size" tooltip={t("general.opcUaEventMonitorQueueHelp")} />
          <InputNumber
            className="generalStyles__width100"
            style={{ width: "100%", marginTop: 8 }}
            size="large"
            disabled={disabled}
            min={0}
            value={monitor.queueSize}
            onChange={(v) => onChangeMonitor({ ...monitor, queueSize: v ?? 0 })}
          />
        </div>
      </div>
      <div className="row mt-3">
        <div className="col-12 col-md-6">
          <OpcUaFieldLabel
            label={`Select Clauses (${monitor.selectClauses.length})`}
            tooltip={t("general.opcUaEventMonitorSelectHelp")}
          />
          {monitor.selectClauses.map((clause, index) => (
            <div key={index} className="generalStyles__flex mb-2">
              <div style={{ width: "100%" }}>
                <SimpleAttributeOperandFields
                  operand={clause}
                  onChangeOperand={(updated) => onChangeSelectClauseAt(index, updated)}
                />
              </div>
              <Tooltip title="Remove select clause">
                <Button
                  onClick={() => onRemoveSelectClause(index)}
                  className="buttonStyle__29"
                  shape="circle"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />
              </Tooltip>
            </div>
          ))}
          <Button onClick={onAddSelectClause} className="generalStyles__width100 buttonStyle__28">
            <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
            Add Select Clause
          </Button>
        </div>
        <div className="col-12 col-md-6">
          <OpcUaFieldLabel
            label={`Where Clause (${monitor.whereClause.length} element(s))`}
            tooltip={t("general.opcUaEventMonitorWhereHelp")}
          />
          {monitor.whereClause.map((element, index) => (
            <div key={index} className="generalStyles__flex mb-2">
              <div style={{ width: "100%" }}>
                <ContentFilterElementEditor
                  element={element}
                  onChangeElement={(updated) => onChangeFilterElementAt(index, updated)}
                />
              </div>
              <Tooltip title="Remove filter element">
                <Button
                  onClick={() => onRemoveFilterElement(index)}
                  className="buttonStyle__29"
                  shape="circle"
                  icon={<FontAwesomeIcon icon={faTrash} />}
                />
              </Tooltip>
            </div>
          ))}
          <Button onClick={onAddFilterElement} className="generalStyles__width100 buttonStyle__28">
            <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
            Add Filter Element
          </Button>
        </div>
      </div>
    </div>
  );
}

/** A node split into its `ns=` / identifier tags, like the Mappings row-1 read. */
function NodeTags({ node }: { node: OpcUaNodeIdForm }) {
  const seg = nodeIdSegments(node);
  return (
    <>
      {seg.ns !== null && <Tag style={{ textAlign: "center" }}>{seg.ns}</Tag>}
      <Tag style={{ textAlign: "center" }}>{seg.id}</Tag>
    </>
  );
}

/** The raw reference line's node part: `ns=…;<identifier>` in the Mappings colors. */
function RawNode({ node }: { node: OpcUaNodeIdForm }) {
  const seg = nodeIdSegments(node);
  return (
    <>
      {seg.ns !== null && (
        <>
          <span style={{ color: RAW_COLOR.ns }}>{seg.ns}</span>
          <span>;</span>
        </>
      )}
      <span style={{ color: RAW_COLOR.id }}>{seg.id}</span>
    </>
  );
}

function summarizeMonitor({ monitor }: { monitor: OpcUaEventMonitorForm }) {
  const sub = monitor.subscriptionName || "(default)";
  return {
    tags: (
      <>
        <NodeTags node={monitor.node} />
        <Tag style={{ textAlign: "center" }}>Sub: {sub}</Tag>
        <Tag style={{ textAlign: "center" }}>Queue: {monitor.queueSize}</Tag>
        <Tag style={{ textAlign: "center" }}>{monitor.selectClauses.length} select clause(s)</Tag>
        <Tag style={{ textAlign: "center" }}>{monitor.whereClause.length} where element(s)</Tag>
      </>
    ),
    raw: (
      <>
        <RawNode node={monitor.node} /> — <span style={{ color: RAW_COLOR.value }}>sub={sub}</span>;
        queue={monitor.queueSize};{" "}
        <span style={{ color: RAW_COLOR.msg }}>
          {monitor.selectClauses.length} select clause(s)
        </span>
        ;{" "}
        <span style={{ color: RAW_COLOR.active }}>
          {monitor.whereClause.length} where element(s)
        </span>
      </>
    ),
  };
}

/**
 * The raw reference line's message part. As in the node mappings' LN2 section,
 * the key's enum value reads as its message name ("fault:normal:Door Does Not
 * Close", not "fault:normal:4") and the number moves to the tooltip. A key that
 * doesn't resolve shows as stored.
 */
function RawMessageKey({ msgId }: { msgId: string }) {
  const [t] = useTranslation("global");
  const msgParts = msgId.split(":");
  const optionLabel = getOptionLabel(msgId);
  if (optionLabel === null) return <span style={{ color: RAW_COLOR.msg }}>{msgId}</span>;
  return (
    <Tooltip
      title={t("general.opcUaMappingMessageEnumHelp", {
        name: messageNameOf(msgId),
        value: msgParts[2],
      })}
    >
      <span style={{ color: RAW_COLOR.msg }}>
        {msgParts[0]}:{msgParts[1]}:{optionLabel}
      </span>
    </Tooltip>
  );
}

function summarizeMapping({ row }: { row: OpcUaEventToMessageRow }) {
  const category = getCategory(row.msgId);
  const messageName = messageNameOf(row.msgId);
  return {
    category,
    tags: (
      <>
        {category && (
          <Tag
            style={{
              backgroundColor: categoryColor(category.categoryKey),
              color: "#fff",
              border: "none",
              width: 200,
              textAlign: "center",
            }}
          >
            {category.label}
          </Tag>
        )}
        <Tag style={{ width: 200, textAlign: "center" }}>{messageName}</Tag>
        <Tag color={row.active ? "success" : "default"} style={{ width: 100, textAlign: "center" }}>
          {row.active ? "Active" : "Inactive"}
        </Tag>
        <NodeTags node={row.eventTypeNode} />
      </>
    ),
    raw: (
      <>
        <RawNode node={row.eventTypeNode} /> → <RawMessageKey msgId={row.msgId} />{" "}
        <span style={{ color: row.active ? RAW_COLOR.active : RAW_COLOR.inactive }}>
          ({row.active ? "active" : "inactive"})
        </span>
      </>
    ),
  };
}

/** The event-type / message / active form shared by the create section and an in-place edit. */
function EventMappingForm({
  draft,
  onChangeDraft,
  disabled = false,
}: {
  draft: OpcUaEventToMessageRow;
  onChangeDraft: (next: OpcUaEventToMessageRow) => void;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");
  return (
    <>
      <NodeIdEditor
        node={draft.eventTypeNode}
        onChangeNode={(eventTypeNode) => onChangeDraft({ ...draft, eventTypeNode })}
        label="Event Type"
        disabled={disabled}
      />
      <div className="row mt-2">
        <div className="col-12 col-md-8 mt-2">
          <OpcUaFieldLabel label="LN2 Message" tooltip={t("general.opcUaEventMessageHelp")} />
          <div style={{ marginTop: 8 }}>
            <SelectLn2Message
              value={draft.msgId}
              onChange={(key) => onChangeDraft({ ...draft, msgId: key })}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="col-12 col-md-4 mt-3" style={{ textAlign: "center" }}>
          <OpcUaFieldLabel label="Active" tooltip={t("general.opcUaEventActiveHelp")} />
          <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
            <Switch
              checked={draft.active}
              onChange={(checked) => onChangeDraft({ ...draft, active: checked })}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </>
  );
}

const blankMonitor = (): OpcUaEventMonitorForm => ({
  node: { ...BLANK_NUMERIC_NODE_ID },
  selectClauses: [],
  whereClause: [],
  subscriptionName: "",
  queueSize: 1000,
});

const blankMapping = (): OpcUaEventToMessageRow => ({
  eventTypeNode: { ...BLANK_NUMERIC_NODE_ID },
  msgId: "",
  active: true,
});

/** Events/alarms: event monitors (subscriptions with a SELECT+WHERE filter) and event-type -> LN2 message mappings. */
export default function EventsEditor({
  eventMonitors,
  onChangeEventMonitors,
  eventMappings,
  onChangeEventMappings,
}: {
  eventMonitors: OpcUaEventMonitorForm[];
  onChangeEventMonitors: (rows: OpcUaEventMonitorForm[]) => void;
  eventMappings: OpcUaEventToMessageRow[];
  onChangeEventMappings: (rows: OpcUaEventToMessageRow[]) => void;
}) {
  const [t] = useTranslation("global");
  const [draftMonitor, setDraftMonitor] = useState<OpcUaEventMonitorForm>(blankMonitor);
  const [draftMapping, setDraftMapping] = useState<OpcUaEventToMessageRow>(blankMapping);
  // null = authoring a new row with the create form; an index = editing that
  // stored row in place (its card grows the form, the create form is disabled).
  const [editingMonitorIndex, setEditingMonitorIndex] = useState<number | null>(null);
  const [editingMappingIndex, setEditingMappingIndex] = useState<number | null>(null);

  const canAddMonitor = isNodeComplete(draftMonitor.node);
  const onSubmitMonitor = () => {
    if (!canAddMonitor) return;
    if (editingMonitorIndex !== null) {
      const updated = [...eventMonitors];
      updated[editingMonitorIndex] = draftMonitor;
      onChangeEventMonitors(updated);
      setEditingMonitorIndex(null);
    } else {
      onChangeEventMonitors([...eventMonitors, draftMonitor]);
    }
    setDraftMonitor(blankMonitor());
  };
  const onEditMonitor = (index: number) => {
    setDraftMonitor(eventMonitors[index]);
    setEditingMonitorIndex(index);
  };
  const onCancelEditMonitor = () => {
    setDraftMonitor(blankMonitor());
    setEditingMonitorIndex(null);
  };
  const onRemoveMonitor = (index: number) => {
    onChangeEventMonitors(eventMonitors.filter((_, i) => i !== index));
    if (editingMonitorIndex === index) onCancelEditMonitor();
  };

  const canAddMapping = isNodeComplete(draftMapping.eventTypeNode) && draftMapping.msgId !== "";
  const onSubmitMapping = () => {
    if (!canAddMapping) return;
    if (editingMappingIndex !== null) {
      const updated = [...eventMappings];
      updated[editingMappingIndex] = draftMapping;
      onChangeEventMappings(updated);
      setEditingMappingIndex(null);
    } else {
      onChangeEventMappings([...eventMappings, draftMapping]);
    }
    setDraftMapping(blankMapping());
  };
  const onEditMapping = (index: number) => {
    setDraftMapping(eventMappings[index]);
    setEditingMappingIndex(index);
  };
  const onCancelEditMapping = () => {
    setDraftMapping(blankMapping());
    setEditingMappingIndex(null);
  };
  const onRemoveMapping = (index: number) => {
    onChangeEventMappings(eventMappings.filter((_, i) => i !== index));
    if (editingMappingIndex === index) onCancelEditMapping();
  };

  const monitorEditing = editingMonitorIndex !== null;
  const mappingEditing = editingMappingIndex !== null;

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <Collapse defaultActiveKey={["event-monitors"]}>
        <Panel
          header={t("general.opcUaEventMonitors", { total: eventMonitors.length })}
          key="event-monitors"
        >
          <EventMonitorForm
            monitor={draftMonitor}
            onChangeMonitor={setDraftMonitor}
            disabled={monitorEditing}
          />
          <div className="row mt-3">
            <div className="col-12">
              <Button
                onClick={onSubmitMonitor}
                disabled={!canAddMonitor || monitorEditing}
                className={`generalStyles__width100 ${canAddMonitor && !monitorEditing ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add Event Monitor
              </Button>
            </div>
          </div>
          <br />
          <br />
          <Divider />
          {eventMonitors.map((monitor, index) => {
            const editing = editingMonitorIndex === index;
            // While editing, the summary follows the draft so it reacts to every change.
            const { tags, raw } = summarizeMonitor({ monitor: editing ? draftMonitor : monitor });
            return (
              <EditableRowCard
                key={`${monitor.node.namespaceIndex}-${monitor.node.identifier}-${index}`}
                borderColor="rgba(255, 255, 255, 0.15)"
                tags={tags}
                raw={raw}
                editing={editing}
                dimmed={monitorEditing && !editing}
                canSave={canAddMonitor}
                deleteTitle="Delete this event monitor? This cannot be undone."
                onEdit={() => onEditMonitor(index)}
                onSave={onSubmitMonitor}
                onCancel={onCancelEditMonitor}
                onRemove={() => onRemoveMonitor(index)}
              >
                <EventMonitorForm monitor={draftMonitor} onChangeMonitor={setDraftMonitor} />
              </EditableRowCard>
            );
          })}
        </Panel>
      </Collapse>
      <br />
      <Collapse>
        <Panel
          header={t("general.opcUaEventToMessageMappings", { total: eventMappings.length })}
          key="event-mappings"
        >
          <EventMappingForm
            draft={draftMapping}
            onChangeDraft={setDraftMapping}
            disabled={mappingEditing}
          />
          <div className="row mt-3">
            <div className="col-12">
              <Button
                onClick={onSubmitMapping}
                disabled={!canAddMapping || mappingEditing}
                className={`generalStyles__width100 ${canAddMapping && !mappingEditing ? "buttonStyle__28" : ""}`}
              >
                <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
                Add
              </Button>
            </div>
          </div>
          <br />
          <br />
          <Divider />
          {eventMappings.map((row, index) => {
            const editing = editingMappingIndex === index;
            const { category, tags, raw } = summarizeMapping({ row: editing ? draftMapping : row });
            return (
              <EditableRowCard
                key={`${row.eventTypeNode.namespaceIndex}-${row.eventTypeNode.identifier}-${row.msgId}-${index}`}
                borderColor={
                  category ? categoryBorderColor(category.categoryKey) : "rgba(255, 255, 255, 0.15)"
                }
                tags={tags}
                raw={raw}
                editing={editing}
                dimmed={mappingEditing && !editing}
                canSave={canAddMapping}
                deleteTitle="Delete this mapping? This cannot be undone."
                onEdit={() => onEditMapping(index)}
                onSave={onSubmitMapping}
                onCancel={onCancelEditMapping}
                onRemove={() => onRemoveMapping(index)}
              >
                <EventMappingForm draft={draftMapping} onChangeDraft={setDraftMapping} />
              </EditableRowCard>
            );
          })}
        </Panel>
      </Collapse>
    </div>
  );
}
